import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Award, Download, Eye, Search, RefreshCw, FileText, CheckCircle, Plus, Edit, Ban, Trash2, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getIntegrityIssues = (cert) => {
  const issues = [];
  const title = cert.certification_title || cert.qualification || '';
  if (!title.trim() || /[-–]\s*$/.test(title.trim())) issues.push('Missing/incomplete title');
  if (!(cert.candidate_name || cert.full_name)) issues.push('Missing candidate name');
  if (!cert.certificate_number) issues.push('Missing certificate number');
  if (!(cert.issue_date || cert.issued_at || cert.exam_date)) issues.push('Missing issue date');
  if (!(cert.expiry_date || cert.recertification_due)) issues.push('Missing expiry/recertification date');
  return issues;
};

function CertificatesTab({ token }) {
  const [certs, setCerts] = useState([]);
  const [filteredCerts, setFilteredCerts] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [certSearch, setCertSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('latest');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [form, setForm] = useState({ certificate_number: '', full_name: '', user_id: '', qualification: 'Board Certified in Aesthetic Medicine (IMCAC – CBEAM)', gender: '', country: '', date_of_birth: '', certification_type: 'Full Certification', route_completed: '', exam_date: '', validity_status: 'Active', cme_summary: '', recertification_due: '', level: '' });

  const fetchCerts = async () => {
    try {
      const res = await axios.get(`${API}/admin/certificates`, { headers: { Authorization: `Bearer ${token}` } });
      setCerts(res.data);
      setFilteredCerts(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      // Filter to only candidates
      setUsers(res.data.filter(u => u.role === 'candidate'));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchCerts(); fetchUsers(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  // Filter and sort certificates
  useEffect(() => {
    let result = [...certs];
    
    if (certSearch) {
      const term = certSearch.toLowerCase();
      result = result.filter(c => 
        c.full_name?.toLowerCase().includes(term) ||
        c.certificate_number?.toLowerCase().includes(term) ||
        c.qualification?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      result = result.filter(c => c.validity_status === statusFilter);
    }
    
    if (issuesOnly) {
      result = result.filter(c => getIntegrityIssues(c).length > 0);
    }
    
    result.sort((a, b) => {
      const dateA = new Date(a.issued_date || a.exam_date);
      const dateB = new Date(b.issued_date || b.exam_date);
      return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
    });
    
    setFilteredCerts(result);
  }, [certs, certSearch, statusFilter, sortOrder, issuesOnly]);

  // Filter users for dropdown
  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const selectUser = (user) => {
    setSelectedUser(user);
    setForm({
      ...form,
      user_id: user.id,
      full_name: user.full_name,
      gender: user.gender || '',
      country: user.country || '',
      date_of_birth: user.date_of_birth || ''
    });
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const handleAdd = async () => {
    if (!form.full_name) { toast.error('Full name is required'); return; }
    try {
      await axios.post(`${API}/admin/certificates`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Certificate issued successfully');
      setShowForm(false);
      setSelectedUser(null);
      setForm({ certificate_number: '', full_name: '', user_id: '', qualification: 'Board Certified in Aesthetic Medicine (IMCAC – CBEAM)', gender: '', country: '', date_of_birth: '', certification_type: 'Full Certification', route_completed: '', exam_date: '', validity_status: 'Active', cme_summary: '', recertification_due: '', level: '' });
      fetchCerts();
    } catch (err) { toast.error('Failed to create certificate'); }
  };

  const handleRevoke = async (certId) => {
    if (!window.confirm('Are you sure you want to revoke this certificate?')) return;
    try {
      await axios.put(`${API}/admin/certificates/${certId}`, { validity_status: 'Revoked' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Certificate revoked');
      fetchCerts();
    } catch (err) { toast.error('Failed to revoke certificate'); }
  };

  const handleReissue = async (cert) => {
    try {
      const newCert = {
        ...cert,
        certificate_number: '',
        validity_status: 'Active',
        certification_type: 'Reissued',
        exam_date: new Date().toISOString().split('T')[0]
      };
      delete newCert.id;
      await axios.post(`${API}/admin/certificates`, newCert, { headers: { Authorization: `Bearer ${token}` } });
      // Mark old one as reissued
      await axios.put(`${API}/admin/certificates/${cert.id}`, { validity_status: 'Reissued' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Certificate reissued successfully');
      fetchCerts();
    } catch (err) { toast.error('Failed to reissue certificate'); }
  };

  const handleApproveCertificate = async (certId) => {
    try {
      await axios.put(`${API}/admin/certificates/${certId}`, { 
        validity_status: 'Active',
        issued_at: new Date().toISOString()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Certificate approved and activated');
      fetchCerts();
    } catch (err) { toast.error('Failed to approve certificate'); }
  };

  const handleEdit = async () => {
    if (!editingCert) return;
    try {
      await axios.put(`${API}/admin/certificates/${editingCert.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Certificate updated successfully');
      setEditingCert(null);
      setForm({ certificate_number: '', full_name: '', qualification: 'Board Certified in Aesthetic Medicine (IMCAC – CBEAM)', gender: '', country: '', date_of_birth: '', certification_type: 'Full Certification', route_completed: '', exam_date: '', validity_status: 'Active', cme_summary: '', recertification_due: '', level: '' });
      fetchCerts();
    } catch (err) { toast.error('Failed to update certificate'); }
  };

  const handleDelete = async (certId, certNumber) => {
    if (!window.confirm(`Are you sure you want to delete certificate ${certNumber}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/certificates/${certId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Certificate deleted');
      fetchCerts();
    } catch (err) { toast.error('Failed to delete certificate'); }
  };

  const startEdit = (cert) => {
    setEditingCert(cert);
    setForm({
      certificate_number: cert.certificate_number || '',
      full_name: cert.full_name || '',
      qualification: cert.qualification || '',
      gender: cert.gender || '',
      country: cert.country || '',
      date_of_birth: cert.date_of_birth || '',
      certification_type: cert.certification_type || '',
      route_completed: cert.route_completed || '',
      exam_date: cert.exam_date || '',
      validity_status: cert.validity_status || 'Active',
      cme_summary: cert.cme_summary || '',
      recertification_due: cert.recertification_due || '',
      level: cert.level || ''
    });
    setShowForm(false);
  };

  const downloadPdf = async (certId) => {
    try {
      const res = await axios.get(`${API}/admin/certificates/${certId}/generate-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `IMCAC-Certificate-${certId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) { toast.error('Failed to generate PDF'); }
  };

  return (
    <div data-testid="admin-certificates-tab" className="space-y-4">
      {/* Filters Row */}
      <div className="flex flex-wrap gap-3 items-center bg-slate-50 p-3 rounded-sm">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, certificate number..."
              value={certSearch}
              onChange={(e) => setCertSearch(e.target.value)}
              className="pl-9 rounded-sm text-sm"
              data-testid="cert-search"
            />
          </div>
        </div>
        <div className="w-[150px]">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-sm text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Pending Approval">Pending Approval</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
              <SelectItem value="Revoked">Revoked</SelectItem>
              <SelectItem value="Reissued">Reissued</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-[150px]">
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="rounded-sm text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest">Latest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={() => { setShowForm(!showForm); setEditingCert(null); setSelectedUser(null); setForm({ certificate_number: '', full_name: '', user_id: '', qualification: 'Board Certified in Aesthetic Medicine (IMCAC – CBEAM)', gender: '', country: '', date_of_birth: '', certification_type: 'Full Certification', route_completed: '', exam_date: '', validity_status: 'Active', cme_summary: '', recertification_due: '', level: '' }); }} 
          className="bg-[#1B2A4A] text-white rounded-sm text-sm" 
          data-testid="add-cert-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Issue Certificate
        </Button>
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-xs text-slate-500 items-center flex-wrap">
        <span>Total: <strong>{certs.length}</strong></span>
        <span>Pending: <strong className="text-amber-600">{certs.filter(c => c.validity_status === 'Pending Approval').length}</strong></span>
        <span>Active: <strong className="text-green-600">{certs.filter(c => c.validity_status === 'Active').length}</strong></span>
        <span>Revoked: <strong className="text-red-600">{certs.filter(c => c.validity_status === 'Revoked').length}</strong></span>
        <span>Showing: <strong>{filteredCerts.length}</strong></span>
        {certs.filter(c => getIntegrityIssues(c).length > 0).length > 0 && (
          <button
            onClick={() => setIssuesOnly(!issuesOnly)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm border transition-colors ${issuesOnly ? 'bg-amber-100 border-amber-400 text-amber-800' : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'}`}
            title="Certificates with missing titles, names, or dates. Click to filter."
            data-testid="data-issues-toggle"
          >
            <AlertTriangle className="h-3 w-3" />
            Data Issues: <strong>{certs.filter(c => getIntegrityIssues(c).length > 0).length}</strong>
            {issuesOnly && <X className="h-3 w-3 ml-1" />}
          </button>
        )}
      </div>

      {/* Edit Certificate Form */}
      {editingCert && (
        <Card className="imcac-card mb-4 border-2 border-amber-400" data-testid="edit-cert-form">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-[#1B2A4A]">Edit Certificate: {editingCert.certificate_number}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingCert(null)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full Name</Label><Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
              <div>
                <Label className="text-xs">Qualification</Label>
                <Select value={form.qualification} onValueChange={v => setForm({...form, qualification: v})}>
                  <SelectTrigger className="rounded-sm mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Board Certified in Aesthetic Medicine (IMCAC – CBEAM)">CBEAM - Aesthetic Medicine</SelectItem>
                    <SelectItem value="Board Certified in Longevity Medicine (IMCAC – CBELM)">CBELM - Longevity Medicine</SelectItem>
                    <SelectItem value="Board Certified in Regenerative Medicine (IMCAC – CBERM)">CBERM - Regenerative Medicine</SelectItem>
                    <SelectItem value="Board Certified in Aesthetic Dentistry & Facial Aesthetics (IMCAC – CBEAD)">CBEAD - Aesthetic Dentistry</SelectItem>
                    <SelectItem value="Board Certified in Aesthetic Clinical Nursing (IMCAC – CBENA)">CBENA - Aesthetic Clinical Nursing</SelectItem>
                    <SelectItem value="Certified in Clinical Aesthetic Assistance (IMCAC – CBEAA)">CBEAA - Clinical Aesthetic Assistance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Country</Label><Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
              <div><Label className="text-xs">Route Completed</Label><Input value={form.route_completed} onChange={e => setForm({...form, route_completed: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
              <div><Label className="text-xs">Exam Date</Label><Input type="date" value={form.exam_date} onChange={e => setForm({...form, exam_date: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
              <div>
                <Label className="text-xs">Validity Status</Label>
                <Select value={form.validity_status} onValueChange={v => setForm({...form, validity_status: v})}>
                  <SelectTrigger className="rounded-sm mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="Suspended">Suspended</SelectItem>
                    <SelectItem value="Revoked">Revoked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Recertification Due</Label><Input type="date" value={form.recertification_due} onChange={e => setForm({...form, recertification_due: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleEdit} className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm text-sm"><Edit className="h-4 w-4 mr-1" /> Update Certificate</Button>
              <Button variant="outline" onClick={() => setEditingCert(null)} className="rounded-sm text-sm">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="imcac-card mb-4" data-testid="add-cert-form">
          <CardContent className="p-5 space-y-3">
            <h3 className="text-lg font-semibold text-[#1B2A4A] mb-3">Issue New Certificate</h3>
            
            {/* User Selection */}
            <div className="bg-blue-50 p-3 rounded-sm mb-3">
              <Label className="text-xs font-semibold text-blue-700">Step 1: Select User (Optional)</Label>
              <p className="text-xs text-slate-500 mb-2">Search and select a registered user to auto-fill their details</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                  onFocus={() => setShowUserDropdown(true)}
                  className="pl-9 rounded-sm text-sm"
                  data-testid="user-search"
                />
                {showUserDropdown && userSearch && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded-sm shadow-lg max-h-48 overflow-y-auto">
                    {filteredUsers.length === 0 ? (
                      <p className="p-2 text-xs text-slate-500">No users found</p>
                    ) : (
                      filteredUsers.slice(0, 10).map(u => (
                        <div
                          key={u.id}
                          className="p-2 hover:bg-slate-100 cursor-pointer border-b"
                          onClick={() => selectUser(u)}
                        >
                          <p className="text-sm font-medium text-[#1B2A4A]">{u.full_name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              {selectedUser && (
                <div className="mt-2 p-2 bg-green-50 rounded-sm flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-700">{selectedUser.full_name}</p>
                    <p className="text-xs text-green-600">{selectedUser.email}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setForm({...form, user_id: '', full_name: ''}); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Certificate Details */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Certificate Number (auto-generated if blank)</Label><Input value={form.certificate_number} onChange={e => setForm({...form, certificate_number: e.target.value})} placeholder="Auto-generated" className="rounded-sm mt-1 text-sm" data-testid="cert-number" /></div>
              <div><Label className="text-xs">Full Name *</Label><Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="Dr. Full Name" className="rounded-sm mt-1 text-sm" data-testid="cert-name" /></div>
              <div>
                <Label className="text-xs">Qualification / Examination *</Label>
                <Select value={form.qualification} onValueChange={v => setForm({...form, qualification: v})}>
                  <SelectTrigger className="rounded-sm mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Board Certified in Aesthetic Medicine (IMCAC – CBEAM)">CBEAM - Aesthetic Medicine</SelectItem>
                    <SelectItem value="Board Certified in Longevity Medicine (IMCAC – CBELM)">CBELM - Longevity Medicine</SelectItem>
                    <SelectItem value="Board Certified in Regenerative Medicine (IMCAC – CBERM)">CBERM - Regenerative Medicine</SelectItem>
                    <SelectItem value="Board Certified in Aesthetic Dentistry & Facial Aesthetics (IMCAC – CBEAD)">CBEAD - Aesthetic Dentistry</SelectItem>
                    <SelectItem value="Board Certified in Aesthetic Clinical Nursing (IMCAC – CBENA)">CBENA - Aesthetic Clinical Nursing</SelectItem>
                    <SelectItem value="Certified in Clinical Aesthetic Assistance (IMCAC – CBEAA)">CBEAA - Clinical Aesthetic Assistance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">IPCF Level</Label>
                <Select value={form.level} onValueChange={v => setForm({...form, level: v})}>
                  <SelectTrigger className="rounded-sm mt-1 text-sm"><SelectValue placeholder="Select level" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Level 4 - Certified Aesthetic Clinical Assistant">Level 4</SelectItem>
                    <SelectItem value="Level 5 - Advanced Aesthetic Assistant">Level 5</SelectItem>
                    <SelectItem value="Level 6 - Professional Clinical Certification">Level 6</SelectItem>
                    <SelectItem value="Level 7 - Board-Certified Advanced Clinical Level">Level 7</SelectItem>
                    <SelectItem value="Level 8 - Fellowship & Academic Leadership Level">Level 8</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Gender</Label><Input value={form.gender} onChange={e => setForm({...form, gender: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
              <div><Label className="text-xs">Country</Label><Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
              <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
              <div><Label className="text-xs">Route Completed</Label><Input value={form.route_completed} onChange={e => setForm({...form, route_completed: e.target.value})} placeholder="Route A - Full Examination" className="rounded-sm mt-1 text-sm" /></div>
              <div><Label className="text-xs">Exam Date</Label><Input type="date" value={form.exam_date} onChange={e => setForm({...form, exam_date: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
              <div><Label className="text-xs">Recertification Due</Label><Input type="date" value={form.recertification_due} onChange={e => setForm({...form, recertification_due: e.target.value})} className="rounded-sm mt-1 text-sm" /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAdd} className="bg-[#C5A572] text-white rounded-sm text-sm" data-testid="save-cert-btn">Issue Certificate</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-sm text-sm">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead className="text-xs font-bold">Certificate #</TableHead>
              <TableHead className="text-xs font-bold">Name</TableHead>
              <TableHead className="text-xs font-bold">Country</TableHead>
              <TableHead className="text-xs font-bold">Route</TableHead>
              <TableHead className="text-xs font-bold">Status</TableHead>
              <TableHead className="text-xs font-bold">Issued</TableHead>
              <TableHead className="text-xs font-bold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCerts.map((c) => {
              const issues = getIntegrityIssues(c);
              return (
              <TableRow key={c.id} className={editingCert?.id === c.id ? 'bg-amber-50' : ''}>
                <TableCell className="text-xs font-mono text-slate-600">
                  <span className="inline-flex items-center gap-1.5">
                    {c.certificate_number}
                    {issues.length > 0 && (
                      <span title={`Data issues: ${issues.join('; ')}`} data-testid={`cert-issues-${c.id}`}>
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-sm font-medium text-[#1B2A4A]">{c.full_name || c.candidate_name}</TableCell>
                <TableCell className="text-sm text-slate-600">{c.country}</TableCell>
                <TableCell className="text-xs text-slate-500">{c.route_completed}</TableCell>
                <TableCell><Badge className={`text-xs rounded-sm ${c.validity_status === 'Active' ? 'bg-green-100 text-green-700' : c.validity_status === 'Pending Approval' ? 'bg-amber-100 text-amber-700' : c.validity_status === 'Reissued' ? 'bg-blue-100 text-blue-700' : c.validity_status === 'Suspended' ? 'bg-purple-100 text-purple-700' : 'bg-red-100 text-red-700'}`}>{c.validity_status}</Badge></TableCell>
                <TableCell className="text-xs text-slate-500">{c.issued_at?.split('T')[0]}</TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs rounded-sm" onClick={() => downloadPdf(c.id)} data-testid={`download-pdf-${c.id}`}>
                      <Download className="h-3 w-3 mr-1" /> PDF
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs rounded-sm border-amber-400 text-amber-600 hover:bg-amber-50" onClick={() => startEdit(c)} data-testid={`edit-cert-${c.id}`}>
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    {c.validity_status === 'Pending Approval' && (
                      <Button size="sm" className="h-7 px-2 text-xs rounded-sm bg-green-600 text-white hover:bg-green-700" onClick={() => handleApproveCertificate(c.id)} data-testid={`approve-cert-${c.id}`}>
                        <CheckCircle className="h-3 w-3 mr-1" /> Approve
                      </Button>
                    )}
                    {c.validity_status === 'Active' && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs rounded-sm border-orange-400 text-orange-600 hover:bg-orange-50" onClick={() => handleRevoke(c.id)} data-testid={`revoke-cert-${c.id}`}>
                        <Ban className="h-3 w-3 mr-1" /> Revoke
                      </Button>
                    )}
                    {(c.validity_status === 'Revoked' || c.validity_status === 'Expired') && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs rounded-sm border-green-400 text-green-600 hover:bg-green-50" onClick={() => handleReissue(c)} data-testid={`reissue-cert-${c.id}`}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Reissue
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs rounded-sm border-red-400 text-red-600 hover:bg-red-50" onClick={() => handleDelete(c.id, c.certificate_number)} data-testid={`delete-cert-${c.id}`}>
                      <Trash2 className="h-3 w-3 mr-1" /> Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default CertificatesTab;
